import type { RadioGroupItemProps } from '@radix-ui/react-radio-group';

import { forwardRef, Fragment, useId } from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import { CheckIcon } from '@paid-ui/icons/check';
import { CircleIcon } from '@paid-ui/icons/circle';

export interface RadioGroupProps extends RadioGroupPrimitive.RadioGroupProps {
  error?: boolean;
  changed?: boolean;
  onEnter?: () => void;
}

export const radioCardVariants = cva(
  'flex w-full cursor-pointer select-none items-center gap-x-2.5 rounded-2xl rounded-tl-none border bg-white px-5 py-4 text-left text-grey-dark transition-all hover:border-grey-tertiary hover:bg-grey-light hover:text-black disabled:bg-grey-light disabled:text-grey-dark',
  {
    variants: {
      selected: {
        true: 'border-blue shadow-field disabled:border-blue disabled:shadow-field disabled:hover:shadow-field',
        false:
          'border-grey-mid shadow-none disabled:border-grey-mid disabled:shadow-none disabled:hover:shadow-none',
      },
    },
  },
);

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ error, changed, hidden, onEnter, onKeyDown, onChange, ...restProps }, ref) => {
    if (hidden) {
      return null;
    }

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
      onKeyDown?.(event);
      if (event.key === 'Enter') {
        onEnter?.();
      }
    };

    return <RadioGroupPrimitive.Root ref={ref} onKeyDown={handleKeyDown} {...restProps} />;
  },
);

export const RadioGroupItem = forwardRef<HTMLButtonElement, RadioGroupItemProps>(
  ({ id: customId, className, hidden, children, ...restProps }, ref) => {
    const fallbackId = useId();

    if (hidden) {
      return null;
    }

    const id = customId ?? fallbackId;
    const Container = children ? 'div' : Fragment;

    return (
      <Container className="flex gap-x-2">
        <RadioGroupPrimitive.Item
          ref={ref}
          id={id}
          aria-labelledby={`${id}-label`}
          className="peer my-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-grey-dark bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-blue data-[state=checked]:text-blue"
          {...restProps}
        >
          <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
            <CircleIcon className="h-2.5 w-2.5 fill-current text-current" />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
        {children ? (
          <LabelPrimitive.Root
            htmlFor={id}
            id={`${id}-label`}
            className={twMerge(
              'cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:select-none peer-disabled:opacity-50',
              className,
            )}
          >
            {children}
          </LabelPrimitive.Root>
        ) : null}
      </Container>
    );
  },
);

export interface RadioGroupCardProps extends RadioGroupItemProps {
  compact?: boolean;
}

export const RadioGroupCard = forwardRef<HTMLButtonElement, RadioGroupCardProps>(
  ({ id: customId, className, hidden, children, compact, ...restProps }, ref) => {
    const fallbackId = useId();

    if (hidden) {
      return null;
    }

    const id = customId ?? fallbackId;
    const Container = children ? 'div' : Fragment;

    const indicatorVariants = cva(
      'peer absolute right-5 top-[20px] h-5 w-5 shrink-0 rounded-full border border-grey-tertiary bg-white text-transparent focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-0 data-[state=checked]:bg-blue data-[state=checked]:text-white',
      {
        variants: {
          compact: {
            true: '!top-[11px]',
          },
        },
      },
    );

    const itemVariants = cva(
      'flex h-full w-full cursor-pointer flex-col gap-2 rounded-2xl rounded-tl-none border-2 border-grey-mid py-4 pl-5 pr-14 transition-colors duration-150 ease-linear hover:border-grey-tertiary peer-disabled:cursor-not-allowed peer-disabled:select-none peer-disabled:border-grey-mid peer-disabled:opacity-50 peer-data-[state=checked]:border-blue peer-data-[state=checked]:font-medium',
      {
        variants: {
          compact: {
            true: 'border-[1px] py-[9px]',
          },
        },
      },
    );

    return (
      <Container className="relative">
        <RadioGroupPrimitive.Item
          ref={ref}
          id={id}
          aria-labelledby={`${id}-label`}
          className={indicatorVariants({ compact })}
          {...restProps}
        >
          <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
            <CheckIcon className={twMerge('h-3.5 w-3.5 text-current')} strokeWidth={3} />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
        {children ? (
          <LabelPrimitive.Root
            htmlFor={id}
            id={`${id}-label`}
            className={twMerge(itemVariants({ compact }), className)}
          >
            {children}
          </LabelPrimitive.Root>
        ) : null}
      </Container>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';
RadioGroupItem.displayName = 'RadioGroupItem';
RadioGroupCard.displayName = 'RadioGroupCard';

export { type RadioGroupItemProps } from '@radix-ui/react-radio-group';
